import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Checkbox,
    Divider,
    Form,
    Icon,
    Input,
    Typography,
    Row,
} from 'antd';
import FormStepTitle from '../FormStepTitle';
import ExternalLink from '../../ExternalLink';
import PaymentCheckout from '../../booking/PaymentCheckout';
import { required } from '../../../constants/formValidation';
import {
    calculateCharges,
    convertTimestampToMoment,
} from '../../../utils';


const propTypes = {
    submitForm: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
};

const { Paragraph, Text } = Typography;

class FormStepBilling extends Component {
    state = {
        hasAgreedTerms: false,
        canSubmitForm: false,
        usePropertyAddressForBilling: true,
    };

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps !== this.props ||
            prevState.hasAgreedTerms !== this.state.hasAgreedTerms ||
            prevState.usePropertyAddressForBilling !== this.state.usePropertyAddressForBilling
        ) {
            this.validateCanSubmitForm();
        }
    }

    toggleAgreeTerms = () => {
        this.setState(prevState => ({ hasAgreedTerms: !prevState.hasAgreedTerms }));
    }

    onBillingAddressCheckboxChange = e => {
        if (!e.target.checked) {
            this.setState({ hasAgreedTerms: false });
        }

        this.setState({
            usePropertyAddressForBilling: !this.state.usePropertyAddressForBilling
        });
    }

    onToken = token => {
        const { usePropertyAddressForBilling } = this.state;
        const { form, submitForm } = this.props;

        form.validateFields((err, values) => {
            submitForm({
                billingAddressLine1: values.billingAddressLine1,
                billingAddressLine2: values.billingAddressLine2,
                billingCity: values.billingCity,
                billingPostcode: values.billingPostcode,
                usePropertyAddressForBilling,
            }, token);
        });
    }

    validateCanSubmitForm = () => {
        const { hasAgreedTerms, usePropertyAddressForBilling } = this.state;
        const { getFieldValue } = this.props.form;
        let canSubmitForm = true;

        if (!usePropertyAddressForBilling) {
            canSubmitForm = getFieldValue('billingAddressLine1') &&
                getFieldValue('billingCity') &&
                getFieldValue('billingPostcode');
        }

        if (!hasAgreedTerms) {
            canSubmitForm = false;
        }

        this.setState({ canSubmitForm });
    }

    render() {
        const { canSubmitForm, usePropertyAddressForBilling, hasAgreedTerms } = this.state;
        const { isLoading, formItemLayout, form, formValues } = this.props;
        const { getFieldDecorator } = form;
        const service = _.get(this.props, 'formValues.service');
        const bookingDateTime = _.get(this.props, 'formValues.bookingDateTime');
        const addressLine1 = _.get(this.props, 'formValues.addressLine1');
        const city = _.get(this.props, 'formValues.city');
        const postcode = _.get(this.props, 'formValues.postcode');
        const emailAddress = _.get(this.props, 'formValues.emailAddress');
        const addonsList = _.get(this.props, 'formValues.addonsList', []);

        const charges = calculateCharges(formValues);

        return (
            <Form {...formItemLayout}>
                <FormStepTitle label="Enter your payment details" />
                <Form.Item>
                    <Checkbox
                        checked={usePropertyAddressForBilling}
                        onChange={this.onBillingAddressCheckboxChange}
                    >
                        Use property address as billing address?
                    </Checkbox>
                </Form.Item>
                {
                    !usePropertyAddressForBilling &&
                    <>
                        <FormStepTitle label="Enter your billing address" />
                        <Form.Item label="Address Line 1">
                            {
                                getFieldDecorator('billingAddressLine1', required())(
                                    <Input />
                                )
                            }
                        </Form.Item>
                        <Form.Item label="Address Line 2">
                            {
                                getFieldDecorator('billingAddressLine2')(
                                    <Input />
                                )
                            }
                        </Form.Item>
                        <Form.Item label="Town / City">
                            {
                                getFieldDecorator('billingCity', required())(
                                    <Input />
                                )
                            }
                        </Form.Item>
                        <Form.Item label="Postcode">
                            {
                                getFieldDecorator('billingPostcode', required())(
                                    <Input />
                                )
                            }
                        </Form.Item>
                    </>
                }
                <Divider />
                <FormStepTitle label="Booking summary" />
                <Form.Item>
                    <Paragraph>
                        <Text><b>Selected Service: </b>{ `${service} - £${charges.service_charge.toFixed(2)}` }</Text>
                        <br />
                        {
                            charges.addons_charge > 0 &&
                            <>
                                <Text><b>Addons: </b>{ `${addonsList.join(', ')} - £${charges.addons_charge.toFixed(2)}` }</Text>
                                <br />
                            </>
                        }
                        <Text><b>Booking Charge: </b>{ `£${charges.booking_charge.toFixed(2)}` }</Text>
                        <br/>
                        <Text><b>Date & Time: </b>{ convertTimestampToMoment(bookingDateTime).format('LLLL') }</Text>
                        <br />
                        <Text><b>Property Address: </b>{ `${addressLine1}, ${city}, ${postcode}` }</Text>
                    </Paragraph>
                </Form.Item>
                <Row>
                    <span className="ant-form-text" style={{ lineHeight: '21px' }}>
                        We require 48 hrs cancellation notice prior to your scheduled booking in order to refund your payment. Cancellation within 48 hrs of your booking will not result in a refund.
                    </span>
                    <br/>
                    <br />
                    <Checkbox onChange={this.toggleAgreeTerms} disabled={isLoading} checked={hasAgreedTerms}>
                        I acknowledge and accept that I have read and agree to the&nbsp;
                        <ExternalLink urlKey="TERMS">Terms & Conditions</ExternalLink>
                        { ' and ' }
                        <ExternalLink urlKey="PRIVACY">Privacy Policy</ExternalLink>
                    </Checkbox>
                </Row>
                <br />
                <PaymentCheckout
                    name={service}
                    onToken={this.onToken}
                    formValues={formValues}
                    email={emailAddress}
                    buttonProps={{
                        loading: isLoading,
                        disabled: !canSubmitForm,
                    }}
                />
                <Row>
                    <ExternalLink urlKey="STRIPE">
                        <Icon type="lock" /> Powered by Stripe for fast, secure payments.
                    </ExternalLink>
                </Row>
            </Form>
        );
    }
}

FormStepBilling.propTypes = propTypes;

export default Form.create({ name: 'confirmBooking' })(FormStepBilling);
